/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import FiltersStep from './FiltersStep'
import ResultsStep from './ResultsStep'
import DailyTrackingMatrix from './DailyTrackingMatrix'
import { fetchValidacaoSellout, fetchAcompanhamentoDiario } from './service'
import {
  ValidacaoSelloutParams,
  ValidacaoSelloutResponse,
  AcompanhamentoDiarioResponse,
  AcompanhamentoDiarioParams,
} from './types'

type Step = 'filters' | 'results' | 'daily_results'

interface Props {
  onToggleHeader?: (hide: boolean) => void
}

export default function ValidacaoSellout({ onToggleHeader }: Props) {
  const [step, setStep] = useState<Step>('filters')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<ValidacaoSelloutResponse | null>(null)
  const [params, setParams] = useState<ValidacaoSelloutParams | null>(null)

  // Estado para o Acompanhamento Diário
  const [dailyData, setDailyData] = useState<AcompanhamentoDiarioResponse | null>(null)
  const [dailyParams, setDailyParams] = useState<AcompanhamentoDiarioParams | null>(null)

  useEffect(() => {
    onToggleHeader?.(step === 'results' || step === 'daily_results')
    return () => {
      onToggleHeader?.(false)
    }
  }, [step, onToggleHeader])

  const handleGenerate = async (selectedParams: ValidacaoSelloutParams) => {
    setParams(selectedParams)

    if (selectedParams.tipoPeriodo === 'diario') {
      setLoading(true)
      try {
        const dParams: AcompanhamentoDiarioParams = {
          distribuidorIds: selectedParams.distribuidorIds,
          ano: selectedParams.ano,
          mes: selectedParams.mes,
          modoAgrupamento: selectedParams.modoAgrupamentoDiario || 'consolidado',
        }
        const dailyResult = await fetchAcompanhamentoDiario(dParams)
        setDailyData(dailyResult)
        setDailyParams(dParams)
        setStep('daily_results')
        if (dailyResult.vendasDiarias.length === 0) {
          toast.info('Nenhuma movimentação diária encontrada para o período selecionado.')
        }
      } catch (err: any) {
        console.error('[ValidacaoSellout] Erro ao carregar acompanhamento diário:', err)
        const errorMsg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Erro ao carregar dados do acompanhamento diário.'
        toast.error(errorMsg)
        setStep('filters')
      } finally {
        setLoading(false)
      }
      return
    }

    setStep('results')
    setLoading(true)
    try {
      const result = await fetchValidacaoSellout(selectedParams)
      setData(result)
    } catch (err: any) {
      console.error('[ValidacaoSellout] Erro ao carregar dados:', err)
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Erro ao carregar dados da validação de sellout.'
      toast.error(errorMsg)
      setStep('filters')
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => {
    setStep('filters')
  }

  if (step === 'daily_results' && dailyData && dailyParams) {
    return (
      <DailyTrackingMatrix
        data={dailyData}
        params={dailyParams}
        onBack={handleBack}
      />
    )
  }

  if (step === 'results' && data) {
    return (
      <ResultsStep
        data={data}
        params={params!}
        loading={loading}
        onBack={handleBack}
      />
    )
  }

  if (step === 'results' && loading) {
    return (
      <ResultsStep
        data={{
          totalSelloutRS: 0,
          totalSelloutCX: 0,
          totalEstoqueCX: 0,
          totalEstoqueRS: 0,
          selloutPorFamilia: [],
          estoquePorFamilia: [],
          coberturaPorFamilia: [],
        }}
        params={params!}
        loading={true}
        onBack={handleBack}
      />
    )
  }

  return <FiltersStep onGenerate={handleGenerate} initialParams={params} />
}
